<template>
  <canvas class="canvas" ref="canvas" :class="{ active: active }"></canvas>
</template>

<script>
import { fabric } from 'fabric'
import debounce from 'lodash/debounce'

const colorStyles = {
  fill: 'transparent',
  stroke: '#0000ff'
}

export default {
  props: ['active', 'shape', 'width', 'height', 'coordinatesInputs'],
  data() {
    return {
      canvas: null,
      ctx: null,
      el: null,
      coordinates: {},
      is_equal: false,
      obj: null,

      move: {
        active: false,
        coordinates: [],
        points: [],
        activeLine: null
      },
      ticks: 0,
      first_coord: null,
      strokeWidth: 1
    }
  },
  mounted() {
    this.canvas = new fabric.Canvas(this.$refs['canvas'])
    this.canvas.setWidth(this.width)
    this.canvas.setHeight(this.height)

    this.canvas.on({
      'mouse:down': this.tick,
      'mouse:move': this.mousemove,
      'object:moving': this.updateCoords,
      'object:scaling': this.updateCoords,
      'object:resizing': this.updateCoords,
      'object:rotating': this.updateCoords
    })
    this.el = this.$el.parentNode
    if (this.coordinatesInputs && this.coordinatesInputs.length >= 2) {
      this.coordinates = this.$lodash.cloneDeep(this.coordinatesInputs)
      setTimeout(this.drawShape)
    }
  },
  methods: {
    tick({ pointer, target }) {
      if (this.active) {
        this.ticks = this.ticks + 1
        let current_coord = {
          x: ((pointer.x * 100) / this.el.clientWidth).toString(),
          y: ((pointer.y * 100) / this.el.clientHeight).toString()
        }
        if (this.shape === 'polygon') {
          this.move.active = true

          if (target && !target.lineIn) {
            // Finish polygon
            this.move.points[0].lineIn = this.move.activeLine
            this.move.active = false
            this.move.activeLine = null

            this.emitPolygonCoords()
            this.$nextTick(() => this.$emit('update:active', false))
          } else {
            const lineIn = this.move.activeLine
            const prevPoint = this.move.points.length > 0 && this.move.points[this.move.points.length - 1]
            if (prevPoint) {
              prevPoint.lineOut = lineIn
            }
            this.move.activeLine = this.makeLine([pointer.x, pointer.y, pointer.x, pointer.y])
            const newPoint = this.makeCircle(pointer.x, pointer.y, lineIn, this.move.activeLine)
            this.move.points.push(newPoint)
            this.canvas.add(this.move.activeLine)
            this.canvas.add(newPoint)
          }
          this.canvas.renderAll()
        } else {
          if (this.ticks == 1) {
            this.move.active = true
            this.first_coord = current_coord
          }
          if (this.ticks == 2) {
            this.move.active = false
            this.second_coord = current_coord

            this.$emit('data', {
              coordinates: [this.first_coord, this.second_coord],
              shape: this.shape
            })
            this.drawShapeParametrs(this.first_coord, this.second_coord)
            this.$nextTick(() => this.$emit('update:active', false))
            this.ticks = 0
          }
        }
      } else if (!target) {
        this.$emit('openModal')
      }
    },
    mousemove({ pointer }) {
      if (this.move.active) {
        if (this.shape === 'polygon') {
          this.move.activeLine.set({
            x2: pointer.x,
            y2: pointer.y
          })
          this.canvas.renderAll()
        } else {
          this.drawShapeParametrs(this.first_coord, {
            x: ((pointer.x * 100) / this.el.clientWidth).toString(),
            y: ((pointer.y * 100) / this.el.clientHeight).toString()
          })
        }
      }
    },
    drawShapeParametrs(coord1, coord2) {
      const coordinates = [
        {
          x: (coord1.x * this.el.clientWidth) / 100,
          y: (coord1.y * this.el.clientHeight) / 100
        },
        {
          x: (coord2.x * this.el.clientWidth) / 100,
          y: (coord2.y * this.el.clientHeight) / 100
        }
      ]
      this.draw(coordinates)
    },
    drawShape() {
      if (this.coordinates && this.coordinates.length) {
        const coordinates = this.coordinates.map(coordinate => {
          return {
            x: (coordinate.x * this.el.clientWidth) / 100,
            y: (coordinate.y * this.el.clientHeight) / 100
          }
        })
        this.draw(coordinates)
      }
    },
    draw(coordinates) {
      if (this.shape === 'polygon') {
        this.clearCtx()
        if (coordinates.length > 2 && this.move.points.length === 0) {
          const points = coordinates.map(p => this.makeCircle(p.x, p.y))
          for (let i = 0; i < points.length; i++) {
            const p1 = points[i]
            const p2 = points[i + 1 < points.length ? i + 1 : 0]
            const line = this.makeLine([p1.left, p1.top, p2.left, p2.top])
            p1.lineOut = line
            p2.lineIn = line
            this.canvas.add(line)
            this.canvas.add(p1)
          }
          this.move.points = points
        } else {
          for (let i = 0; i < coordinates.length; i++) {
            const point = this.move.points[i]
            if (point) {
              point.set({
                left: coordinates[i].x,
                top: coordinates[i].y
              })
              this.updatePointLines(point)
            }
          }
        }
      } else if (this.shape === 'line') {
        const data = {
          strokeWidth: 2,
          selectable: false,
          evented: false
        }
        if (!this.obj) {
          const coords = [coordinates[0].x, coordinates[0].y, coordinates[1].x, coordinates[1].y].map(v => Number(v))
          this.obj = new fabric.Line(coords, { ...colorStyles, ...data })
          this.canvas.add(this.obj)
        } else {
          this.obj.set(data)
          this.obj.set({
            ...data,
            left: coordinates[0].x,
            top: coordinates[0].y,
            x2: coordinates[1].x,
            y2: coordinates[1].y
          })
        }
      }
      if (this.shape === 'rectangle') {
        const data = {
          left: Math.min(coordinates[0].x, coordinates[1].x),
          top: Math.min(coordinates[0].y, coordinates[1].y),
          width: Math.abs(coordinates[1].x - coordinates[0].x),
          height: Math.abs(coordinates[1].y - coordinates[0].y),
          strokeWidth: 1,
          scaleX: 1,
          scaleY: 1,
          selectable: false,
          evented: false
        }
        if (!this.obj) {
          this.obj = new fabric.Rect({ ...colorStyles, ...data })
          this.canvas.add(this.obj)
        } else {
          this.obj.set(data)
        }
      }
      this.canvas.renderAll()
    },
    emitPolygonCoords: debounce(function() {
      const coordinates = this.move.points.map(p => ({
        x: ((p.left * 100) / this.el.clientWidth).toString(),
        y: ((p.top * 100) / this.el.clientHeight).toString()
      }))
      this.$emit('data', {
        coordinates,
        shape: this.shape
      })
    }, 1000),
    updatePointLines(p) {
      p.lineIn.set({ x2: p.left, y2: p.top })
      p.lineOut.set({ x1: p.left, y1: p.top })
    },
    updateCoords(e) {
      if (this.shape === 'polygon') {
        this.updatePointLines(e.target)
        this.emitPolygonCoords()
      } else {
        const coordinates = [
          {
            x: ((this.obj.left * 100) / this.el.clientWidth).toString(),
            y: ((this.obj.top * 100) / this.el.clientHeight).toString()
          },
          {
            x: (((this.obj.left + this.obj.width * this.obj.scaleX) * 100) / this.el.clientWidth).toString(),
            y: (((this.obj.top + this.obj.height * this.obj.scaleY) * 100) / this.el.clientHeight).toString()
          }
        ]
        this.$emit('data', {
          coordinates,
          shape: this.shape
        })
      }
    },
    clearCtx() {
      this.canvas.clear()
      this.obj = null
      this.move.points = []
      this.move.activeLine = null
    },
    makeLine(coords) {
      return new fabric.Line(coords, {
        fill: '#0000ff',
        stroke: '#0000ff',
        strokeWidth: 1,
        selectable: false,
        evented: false
      })
    },
    makeCircle(left, top, lineIn, lineOut) {
      const c = new fabric.Circle({
        left: left,
        top: top,
        strokeWidth: 3,
        radius: 12,
        fill: 'transparent',
        stroke: '#666',
        originY: 'center',
        originX: 'center'
      })
      c.hasControls = c.hasBorders = false

      c.lineIn = lineIn
      c.lineOut = lineOut

      return c
    }
  },
  watch: {
    width(v) {
      this.canvas.setWidth(v)
      this.canvas.renderAll()
      this.$nextTick(this.drawShape)
    },
    height(v) {
      this.canvas.setHeight(v)
      this.canvas.renderAll()
      this.$nextTick(this.drawShape)
    },
    coordinatesInputs: {
      handler(value) {
        if (value && value.length >= 2) {
          this.coordinates = this.$lodash.cloneDeep(value)
          setTimeout(this.drawShape)
        }
      }
    },
    active(value) {
      if (value) {
        this.clearCtx()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.canvas {
  position: absolute;
  top: 0;
  left: 0;
  &.active {
    background: #ffffff55;
    cursor: crosshair;
  }
}
</style>
